import { useStyletron } from '@thanx/uikit/theme'
import type { ServiceCharge } from 'api/serviceCharges'
import { isRecommendedForDelivery } from 'api/serviceCharges'
import TooltipLabel from 'components/TooltipLabel'
import { buildTranslate } from 'locales'
import React from 'react'
import { tableHeaderCellClass, tableShellClass } from '../../tableStyles'
import ServiceChargeRow from './ServiceChargeRow'

const t = buildTranslate(
  'locations.location_list.location_detail.service_charges'
)

const infoButtonAriaLabel = (label: string): string =>
  t('table.info_button_aria', { label }) as string

const recommendedFirst = (charges: ServiceCharge[]): ServiceCharge[] => [
  ...charges.filter(isRecommendedForDelivery),
  ...charges.filter(charge => !isRecommendedForDelivery(charge)),
]

type Props = {
  charges: ServiceCharge[]
  editDisabled: boolean
  editDisabledReason: string | undefined
  onToggleThanxEnabled: (charge: ServiceCharge, next: boolean) => void
  onToggleEligibleForRedemption: (charge: ServiceCharge, next: boolean) => void
  editingId: number | null
  onStartEditing: (charge: ServiceCharge) => void
  lockedIds: Set<number>
}

const ServiceChargeTable = (props: Props) => {
  const [css] = useStyletron()

  const thClassName = tableHeaderCellClass(css)
  const charges = recommendedFirst(props.charges)

  return (
    <table
      className={`full-width ${tableShellClass(css)}`}
      role="table"
      data-testid="service-charge-table"
    >
      <thead>
        <tr>
          <th scope="col" className={thClassName}>
            {t('table.service_charge')}
          </th>
          <th scope="col" className={thClassName}>
            {t('table.calculation')}
          </th>
          <th scope="col" className={thClassName}>
            <TooltipLabel
              label={t('table.applies_to')}
              tooltip={t('table.applies_to_help') as string}
              ariaLabel={infoButtonAriaLabel(t('table.applies_to') as string)}
            />
          </th>
          <th scope="col" className={thClassName}>
            <TooltipLabel
              label={t('table.thanx_enabled')}
              tooltip={t('table.thanx_enabled_help') as string}
              ariaLabel={infoButtonAriaLabel(
                t('table.thanx_enabled') as string
              )}
            />
          </th>
          <th scope="col" className={thClassName}>
            <TooltipLabel
              label={t('table.eligible_for_redemption')}
              tooltip={t('table.eligible_for_redemption_help') as string}
              ariaLabel={infoButtonAriaLabel(
                t('table.eligible_for_redemption') as string
              )}
            />
          </th>
          <th scope="col" className={thClassName}>
            <span className="sr-only">{t('table.actions')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {charges.map((charge, index) => (
          <ServiceChargeRow
            key={charge.id}
            charge={charge}
            isLastRow={index === charges.length - 1}
            {...props}
          />
        ))}
      </tbody>
    </table>
  )
}

export default ServiceChargeTable
